<script lang="ts" setup name="AdminScheduleRecipientListNewSailingSchedule">
import { reactive, ref } from 'vue';
import { useVModel } from '@vueuse/core';

import {
  getCategoricalColors,
  getCategoricalTagColors,
} from '~/features/useColors';
import { useOnResize } from '~/features/useOnResize';
import { useSailingScheduleTradeColoring } from '~/features/useSailingSchedule';

import type { HTTP } from '~/services/HTTP';

import type {
  ISailingScheduleTradeLaneSettings,
  SailingScheduleTradeLane,
  SailingScheduleVoyages,
} from '~/types';

import BaseCardNoResults from './BaseCardNoResults.vue';
import RecipientListSailingScheduleTradeLaneCollapsible from './RecipientListSailingScheduleTradeLaneCollapsible.vue';
import SailingScheduleContent from './SailingScheduleContent.vue';
import SailingScheduleNoDataCard from './SailingScheduleNoDataCard.vue';
import SailingScheduleVoyageCollapsible from './SailingScheduleVoyageCollapsible.vue';

const props = withDefaults(
  defineProps<{
    tradelanes: string[];
    recipientService: {
      index: () => HTTP;
      indexMobile: () => HTTP;
      voyages: (trade: string) => {
        index: () => HTTP;
      };
    };
    modelValue: ISailingScheduleTradeLaneSettings;
    initialTradeList?: string[];
    isEdit?: boolean;
  }>(),

  {
    initialTradeList: () => [],
  },
);

const emit = defineEmits<{
  'update:modelValue': [ISailingScheduleTradeLaneSettings];
  'update:initialTradeList': string[];
}>();

const inputValue = useVModel(props, 'modelValue', emit);
const initialTradeList = useVModel(props, 'initialTradeList', emit);

const { isOnMobile } = useOnResize();
const { getTradeColor } = useSailingScheduleTradeColoring();

const tradelanes = ref<SailingScheduleTradeLane[]>([]);
const voyageTradelanes = ref<SailingScheduleVoyages[]>([]);

const query = reactive({
  cacheFilters: 0,
  tradelanes: props.tradelanes.map((trade) => ({
    id: trade,
    name: trade,
  })),
});
</script>

<template>
  <div class="">
    <p class="text-body-1">
      Show or hide specific ports and voyages in the shared sailing schedule.
    </p>

    <div class="mt-4">
      <SailingScheduleContent
        v-if="isOnMobile"
        v-model:items="voyageTradelanes"
        v-model:query="query"
        :service="recipientService.indexMobile()"
      >
        <template #no-data>
          <BaseCardNoResults
            styled
            hideClear
            message="There are no voyages available for your selected trades at the moment."
          />
        </template>

        <template #default="{ items }">
          <SailingScheduleVoyageCollapsible
            v-for="voyage in items"
            :key="voyage.tradelane"
            :voyage="voyage"
            v-bind="getTradeColor(voyage.tradelane)"
          />
        </template>
      </SailingScheduleContent>

      <SailingScheduleContent
        v-else
        v-model:items="tradelanes"
        v-model:query="query"
        :service="recipientService.index()"
      >
        <template #no-data>
          <SailingScheduleNoDataCard />
        </template>

        <template #default="{ items }">
          <RecipientListSailingScheduleTradeLaneCollapsible
            v-for="(tradeLane, index) in items"
            :key="tradeLane.tradelane"
            v-model:settings="inputValue"
            :voyageService="
              recipientService.voyages(tradeLane.tradelane).index()
            "
            :trade-lane="tradeLane"
            :tagVariant="getCategoricalTagColors(index)"
            :color="getCategoricalColors(index)"
            :initialTradeList="initialTradeList"
            :isEdit="isEdit!!"
          />
        </template>
      </SailingScheduleContent>
    </div>
  </div>
</template>
